import React, { useState } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

const FAQItem: React.FC<{ question: string; answer: React.ReactNode }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item" style={{ borderBottom: '1px solid #eee', padding: '15px 0' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          background: 'none',
          border: 'none',
          padding: '10px 0',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: '1.1rem',
          fontWeight: 600,
          color: '#333'
        }}
      >
        {question}
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} style={{ fontSize: '0.8rem', color: '#666' }} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ padding: '10px 0 20px', color: '#666', lineHeight: 1.6 }}>
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const About: React.FC = () => {
  const faqs = [
    {
      question: "你们是专业的翻译公司吗？",
      answer: "不是。我们并非商业公司实体，而是一个由在德国留学及工作的中国志趣相投的志愿者组成的协作团队。我们以互助和专业服务的形式，提供高水准的**展会中德翻译**及现场支持。因为没有公司的中介费用，我们能为您提供更具性价比的**翻译陪同**服务。"
    },
    {
      question: "你们的译员都是什么背景？",
      answer: "我们的团队成员主要是德国顶尖理工科大学（如TU Darmstadt, TU Munich, RWTH Aachen）的在读硕士、博士及毕业生。我们会根据您的展品类型（机械、医疗、电子等）指派具有相关专业背景的译员，确保**德国展会中文翻译**的准确性。"
    },
    {
      question: "服务费用是如何计算的？",
      answer: (
        <>
          <p>作为志同道合的专业团队，我们提供高性价比的透明报价：</p>
          <ul style={{ paddingLeft: '20px', margin: '10px 0' }}>
            <li><strong>标准翻译/协助</strong>：通常为 200-350 欧元/天。</li>
          </ul>
        </>
      )
    },
    {
      question: "除了口译，你们还能提供哪些帮助？",
      answer: "我们提供全方位的展会现场支持，包括：展位搭建协调、展品物流清关协助、客户接待、代收集名片、代参展（无需国内人员到场）、以及展后即时的商务跟进协助。"
    },
    {
      question: "如何预约服务？需要提前多久？",
      answer: "建议您至少提前 **2-4 周** 联系我们。尤其是在大型展会期间（如法兰克福汽配展、汉诺威工博会），优质的**展会中德翻译**档期非常紧张。您可以通过页面下方的微信二维码或邮件直接联系我们，确认需求后即可锁定档期。"
    },
    {
      question: "如果展会取消或行程变更怎么办？",
      answer: "我们要建立长期的信任关系。如果因不可抗力导致展会取消，我们不收取任何费用。如果是行程变更，请至少提前 48 小时通知，我们将尽力协调人员安排。"
    }
  ];

  // 生成 FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": typeof faq.answer === 'string' ? faq.answer : "详情请参阅页面内容" // 简化非字符串内容
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>关于我们 | 德国展会中德翻译 | 翻译陪同与现场协助 - DECN</title>
        <meta name="description" content="DECN团队提供专业的德国展会中文翻译与陪同服务。FAQ解答：我们是由留学生组成的专业协作团队，提供高性价比的展会中德翻译、代参展及全德城市（法兰克福、慕尼黑等）现场支持。" />
        <meta name="keywords" content="展会中德翻译, 德国展会中文翻译, 翻译陪同, 德国翻译价格, 德国展会协助常见问题, DECN团队介绍" />
        <link rel="canonical" href="https://decn-exhibition.top/about" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <section className="page-header about-header" style={{
        background: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('${getImageUrl('/images/aboutus.jpg', { width: 1600, format: 'webp', quality: 70 })}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: '120px 0 60px',
        color: '#fff',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1>关于我们</h1>
          <p>深耕德国展会多年，为您提供最专业的翻译陪同与现场协助服务</p>
        </div>
      </section>

      <section className="about-detail">
        <div className="container">
          <div className="about-story">
            <h2>我们的专业团队</h2>
            <div className="about-content">
              <div className="about-image">
                <img
                  src={getImageUrl('/images/team.jpg', { width: 800, format: 'webp', quality: 70 })}
                  loading="lazy"
                  decoding="async"
                  alt="德国展会中德翻译陪同团队"
                  style={{ maxWidth: '100%', height: 'auto', borderRadius: '10px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
                />
              </div>
              <div className="about-text">
                <p><strong>DECN德中展会服务</strong>（DECN Exhibition Services）是由在德优秀中国留学生及职场人士组成的专业协作团队。我们扎根德国多年，深刻理解中德商业文化差异，致力于为出海德国的中国企业提供一站式、高品质的**德国展会中文翻译**及现场支持。</p>
                <p>自成立以来，我们已服务过逾百家中国参展企业，行业涵盖<strong>机械制造、电子通讯、生物医疗、新能源汽车、光伏能源、智能家居及各类消费品</strong>。我们的核心成员大多毕业于达姆施塔特工业大学 (TU Darmstadt)、慕尼黑工业大学 (TUM)、亚琛工业大学 (RWTH Aachen) 等顶尖名校，具备深厚的专业知识背景。</p>
              </div>
            </div>
          </div>

          <div className="team-info">
            <h2>核心优势</h2>
            <div className="team-features">
              <div className="feature-item">
                <h3>精准语言能力</h3>
                <p>成员均具备德语C1/C2水平，确保**展会中德翻译**与商务谈判准确无误。</p>
              </div>
              <div className="feature-item">
                <h3>专业背景匹配</h3>
                <p>按展会行业指派具备相关工程或商业背景的译员，让沟通零障碍。</p>
              </div>
              <div className="feature-item">
                <h3>全德翻译陪同</h3>
                <p>法兰克福、汉诺威、科隆、慕尼黑等主要城市均有驻地人员，提供本地化陪同。</p>
              </div>
              <div className="feature-item">
                <h3>全场景协助</h3>
                <p>从布展搭建到撤展物流，我们是您在现场的得力全能助手。</p>
              </div>
            </div>
          </div>

          <div className="faq-section" style={{ marginTop: '60px', maxWidth: '800px', margin: '60px auto 0' }}>
            <h2 style={{ textAlign: 'center', marginBottom: '40px' }}>常见问题 (FAQ)</h2>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default About;